import {
  ElementRef,
  SecurityContext,
  ChangeDetectorRef,
  Component,
  ViewChild,
  inject,
} from '@angular/core';
import type { SafeUrl } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';

import type { Node as ProsemirrorNode, NodeType } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import { Subject } from 'rxjs';

import type { AlignValue, VideoNodeAttrs, VideoNodeView } from '@core/prosemirror-legacy';
import { VideoNode } from '@core/prosemirror-legacy';
import type { ProsemirrorAngularComponent } from '@core/prosemirror-legacy/angular';

@Component({
  templateUrl: 'video-node-view.component.html',
  styleUrls: ['./video-node-view.component.less'],
  imports: [],
})
export class VideoNodeViewComponent implements ProsemirrorAngularComponent, VideoNodeView {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  private readonly MIN_WIDTH = 100;

  @ViewChild('dom', { static: true }) public dom: ElementRef<HTMLElement>;
  public isEditable: boolean = false;
  public width: number;
  public height: number;
  public pmOnDestroy$ = new Subject<void>();
  public src: string;
  public safeUrl: SafeUrl;
  public align?: AlignValue;
  public figureStyle: string;
  public htmlClass: string | null = null;

  private ratioWH: number;
  private nodeSpec: NodeType;
  private ngOnDestroy$ = new Subject<void>();
  private node: ProsemirrorNode;
  private view: EditorView;

  private getPos: () => number;
  private onResize: (newWidth: number, newHeight: number, position: number) => void;

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getNodeView(
    node: ProsemirrorNode,
    view: EditorView,
    getPos: () => number,
    data: {
      isEditable: boolean;
      videoNodeSelection$: Subject<number | null>;
      nodeType: NodeType;
      onResize: (newWidth: number) => void;
    },
  ): NodeView {
    this.isEditable = data.isEditable;
    this.view = view;
    this.nodeSpec = data.nodeType;
    this.getPos = getPos;
    this.onResize = data.onResize;
    this.update(node);

    return {
      dom: this.dom.nativeElement,
      update: (node: ProsemirrorNode) => this.updateView(node),
      destroy: () => this.pmOnDestroy$.next(),
    };
  }

  public initResize(mousedownEvent: MouseEvent) {
    if (!this.isEditable) return;
    mousedownEvent.preventDefault();
    mousedownEvent.stopPropagation();

    const defaultCursor = document.body.style.cursor;
    const originX = mousedownEvent.clientX;
    const initialWidth = this.width;
    const initialHeight = this.height;
    document.body.style.cursor = 'nwse-resize';

    const resize = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const dx = originX - moveEvent.clientX;
      this.width = Math.max(initialWidth - dx, this.MIN_WIDTH);
      this.height = Math.round(this.width / this.ratioWH);
      this.cdRef.detectChanges();
    };

    const finalize = () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', finalize);

      document.body.style.cursor = defaultCursor;
      if (this.width !== initialWidth || this.height !== initialHeight) {
        this.onResize(this.width, this.height, this.getPos());
      }
    };

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', finalize);
  }

  private updateView(node: ProsemirrorNode) {
    if (VideoNode.is(node)) {
      this.update(node);
      return true;
    }

    return false;
  }

  private update(node: ProsemirrorNode) {
    const { src, width, height, align } = <VideoNodeAttrs>node.attrs;
    const html_class = (<VideoNodeAttrs>node.attrs)['html-class'];

    if (this.node !== node) this.node = node;
    if (this.width !== width) this.width = width;
    if (this.height !== height) this.height = height;
    if (this.src !== src) {
      this.src = src;
      let sanitizedUrl = '';

      try {
        sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, src) || '';
      } catch (e) {
        console.error('Error sanitizing URL:', e);
      }

      // eslint-disable-next-line ban/ban
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(sanitizedUrl);
    }
    if (this.align !== align) this.align = align || undefined;
    this.htmlClass = html_class || null;

    this.setInlineStyles();
    this.ratioWH = +(this.width / this.height).toFixed(2);

    this.cdRef.detectChanges();
  }

  private setInlineStyles() {
    if (this.nodeSpec.spec.alignExtension) {
      this.figureStyle = this.nodeSpec.spec.alignExtension.getStyleSerialization(this.node) || '';
    }
  }
}
